// ═══════════════════════════════════════════════════════════════
// BulkImportModal.js — Import CSV en masse (Session J+2)
// ═══════════════════════════════════════════════════════════════
// State machine 7 états : IDLE → PARSING → PREVIEW → IMPORTING →
//                        DONE | ERROR | CANCELLED
//
// Réutilise mailingApi.bulkImportSubscribersStream (SSE).
// Validation client-side (Papa Parse) AVANT envoi backend pour
// éviter aller-retour inutile.
// ═══════════════════════════════════════════════════════════════

import React, { useEffect, useMemo, useRef, useState } from 'react';
import Papa from 'papaparse';
import { Upload, FileText, X, CheckCircle2, AlertCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from '../ui/Toast';
import * as mailingApi from '../../services/mailingApi';

// Palette miroir MailingPage (violet doux Coffre)
const palette = {
    bg: 'var(--bg)',
    cardBg: 'var(--card-bg)',
    cardBorder: 'var(--card-border)',
    text: 'var(--text)',
    muted: 'var(--text-secondary)',
    accent: '#a78bfa',
    accentBg: 'rgba(167,139,250,0.12)',
    accentSoft: 'rgba(167,139,250,0.05)',
    accentBorder: 'rgba(167,139,250,0.30)',
    success: '#22c55e',
    successBg: 'rgba(34,197,94,0.12)',
    warning: '#fbbf24',
    warningBg: 'rgba(251,191,36,0.13)',
    danger: '#ef4444',
    dangerBg: 'rgba(239,68,68,0.10)',
};

// RFC 5322 simplifié (miroir mailingHelpers.js backend)
const EMAIL_RE = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

// Liste blacklist miroir mailingHelpers.js (13 domaines)
const BLACKLIST = new Set([
    '10minutemail.com', 'mailinator.com', 'guerrillamail.com', 'dispostable.com',
    'sharklasers.com', 'temp-mail.org', 'throwaway.email', 'getnada.com',
    'maildrop.cc', 'yopmail.com', 'fakeinbox.com', 'trbvm.com', 'tempmail.dev',
]);

const STEP = {
    IDLE: 'idle',
    PARSING: 'parsing',
    PREVIEW: 'preview',
    IMPORTING: 'importing',
    DONE: 'done',
    ERROR: 'error',
    CANCELLED: 'cancelled',
};

const COL_KEYS = {
    email: ['email', 'mail', 'courriel', 'e-mail', 'address', 'adresse'],
    name: ['name', 'nom', 'fullname', 'full_name', 'prenom', 'prénom', 'firstname'],
};

function detectColumn(headers, candidates) {
    const norm = headers.map((h) => String(h || '').toLowerCase().trim());
    for (const c of candidates) {
        const idx = norm.indexOf(c);
        if (idx >= 0) return idx;
    }
    return -1;
}

function isValidEmail(e) {
    if (!e) return false;
    if (e.length > 320) return false;
    return EMAIL_RE.test(e);
}

function isBlacklisted(email) {
    const dom = (email.split('@')[1] || '').toLowerCase();
    return BLACKLIST.has(dom);
}

// Parse Papa.parse output → array { email, name, status, reason }
function processRows(rows, headers, hasHeader) {
    const emailIdx = hasHeader ? detectColumn(headers, COL_KEYS.email) : 0;
    const nameIdx = hasHeader ? detectColumn(headers, COL_KEYS.name) : 1;

    if (emailIdx < 0) {
        return { items: [], headers, error: 'Aucune colonne email détectée. Vérifie le header.' };
    }

    const seen = new Set();
    const items = [];
    for (const row of rows) {
        if (!Array.isArray(row)) continue;
        const rawEmail = String(row[emailIdx] || '').trim().toLowerCase();
        const name = nameIdx >= 0 ? String(row[nameIdx] || '').trim() : '';
        if (!rawEmail) continue;

        let status = 'ok';
        let reason = '';
        if (!isValidEmail(rawEmail)) { status = 'invalid'; reason = 'Email invalide'; }
        else if (isBlacklisted(rawEmail)) { status = 'blacklisted'; reason = 'Domaine refusé (jetable)'; }
        else if (seen.has(rawEmail)) { status = 'duplicate'; reason = 'Doublon dans le fichier'; }
        else seen.add(rawEmail);

        items.push({ email: rawEmail, name, status, reason });
    }
    return { items, headers };
}

function BulkImportModal({ listId, listName, onClose, onImported }) {
    const [step, setStep] = useState(STEP.IDLE);
    const [items, setItems] = useState([]);
    const [filename, setFilename] = useState('');
    const [headers, setHeaders] = useState([]);
    const [parseError, setParseError] = useState('');
    const [skipDoubleOptin, setSkipDoubleOptin] = useState(true);
    const [progress, setProgress] = useState({ done: 0, total: 0, current_email: '' });
    const [result, setResult] = useState(null);
    const [errorMsg, setErrorMsg] = useState('');
    const [dragActive, setDragActive] = useState(false);

    const abortRef = useRef(null);
    const fileInputRef = useRef(null);

    useEffect(() => () => abortRef.current?.abort(), []);

    const counters = useMemo(() => {
        let ok = 0, dup = 0, inv = 0, bl = 0;
        for (const it of items) {
            if (it.status === 'ok') ok += 1;
            else if (it.status === 'duplicate') dup += 1;
            else if (it.status === 'invalid') inv += 1;
            else if (it.status === 'blacklisted') bl += 1;
        }
        return { total: items.length, ok, dup, inv, bl };
    }, [items]);

    const validContacts = useMemo(
        () => items.filter((i) => i.status === 'ok').map((i) => ({ email: i.email, name: i.name || null })),
        [items]
    );

    const handleFile = (file) => {
        if (!file) return;
        const name = file.name || '';
        const sizeMb = file.size / (1024 * 1024);
        if (sizeMb > 5) {
            toast.error('Fichier trop volumineux (>5 MB). Scinde-le.');
            return;
        }
        setFilename(name);
        setStep(STEP.PARSING);
        setParseError('');

        Papa.parse(file, {
            delimitersToGuess: [',', ';', '\t', '|'],
            skipEmptyLines: true,
            complete: (res) => {
                if (!res?.data || res.data.length === 0) {
                    setParseError('Fichier vide ou illisible.');
                    setStep(STEP.IDLE);
                    return;
                }
                if (res.data.length > 10000) {
                    setParseError('Maximum 10 000 lignes — scinde ton fichier.');
                    setStep(STEP.IDLE);
                    return;
                }
                // Détection header : 1ère ligne contient-elle un mot-clé email/nom ?
                const firstRow = (res.data[0] || []).map((v) => String(v || '').toLowerCase().trim());
                const hasHeader = firstRow.some((c) => COL_KEYS.email.includes(c) || COL_KEYS.name.includes(c));
                const headerRow = hasHeader ? firstRow : firstRow.map((_, i) => `col_${i}`);
                const dataRows = hasHeader ? res.data.slice(1) : res.data;

                const { items: processed, error } = processRows(dataRows, headerRow, hasHeader);
                if (error) {
                    setParseError(error);
                    setStep(STEP.IDLE);
                    return;
                }
                if (processed.length === 0) {
                    setParseError('Aucun contact détecté. Vérifie la colonne email.');
                    setStep(STEP.IDLE);
                    return;
                }
                setHeaders(headerRow);
                setItems(processed);
                setStep(STEP.PREVIEW);
            },
            error: (err) => {
                setParseError(`Erreur de lecture : ${err?.message || 'inconnu'}`);
                setStep(STEP.IDLE);
            },
        });
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragActive(false);
        const file = e.dataTransfer?.files?.[0];
        handleFile(file);
    };

    const submit = () => {
        if (validContacts.length === 0) {
            toast.warning('Aucun contact valide à importer.');
            return;
        }
        setStep(STEP.IMPORTING);
        setProgress({ done: 0, total: validContacts.length, current_email: '' });

        abortRef.current = mailingApi.bulkImportSubscribersStream(
            listId,
            {
                contacts: validContacts,
                skipDoubleOptin,
                source: 'csv',
                metadata: { filename, total_in_file: items.length, valid: counters.ok },
            },
            {
                onProgress: (p) => {
                    if (p.phase === 'processing') {
                        setProgress({
                            done: p.done || 0,
                            total: p.total || validContacts.length,
                            current_email: p.current_email || '',
                        });
                    }
                },
                onDone: (d) => {
                    setResult(d);
                    setStep(STEP.DONE);
                    toast.success(`${d.created} ajouté${d.created > 1 ? 's' : ''}, ${d.attached_existing} déjà présent${d.attached_existing > 1 ? 's' : ''}, ${d.skipped?.length || 0} ignoré${(d.skipped?.length || 0) > 1 ? 's' : ''}.`);
                    onImported?.();
                },
                onError: (e) => {
                    setErrorMsg(e.error || e.code || 'Erreur inconnue');
                    setStep(STEP.ERROR);
                },
            }
        );
    };

    const cancel = () => {
        abortRef.current?.abort();
        setStep(STEP.CANCELLED);
    };

    const reset = () => {
        setStep(STEP.IDLE);
        setItems([]);
        setHeaders([]);
        setFilename('');
        setParseError('');
        setProgress({ done: 0, total: 0, current_email: '' });
        setResult(null);
        setErrorMsg('');
    };

    return (
        <div onClick={onClose} style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9100, padding: 20,
        }}>
            <div onClick={(e) => e.stopPropagation()} style={{
                background: palette.cardBg, color: palette.text,
                border: `1px solid ${palette.cardBorder}`, borderRadius: 14,
                width: '100%', maxWidth: 720, padding: 24,
                boxShadow: '0 20px 60px rgba(0,0,0,0.45)',
                maxHeight: '85vh', display: 'flex', flexDirection: 'column',
            }}>
                <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18, flexShrink: 0 }}>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: palette.text }}>
                        Importer un fichier CSV
                        {listName && <span style={{ color: palette.muted, fontWeight: 400, marginLeft: 6 }}>· {listName}</span>}
                    </h3>
                    <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: palette.muted, padding: 4 }}>
                        <X size={16} />
                    </button>
                </header>

                <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                    {step === STEP.IDLE && (
                        <IdleStep
                            dragActive={dragActive}
                            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                            onDragLeave={() => setDragActive(false)}
                            onDrop={onDrop}
                            onPick={() => fileInputRef.current?.click()}
                            parseError={parseError}
                        />
                    )}
                    {step === STEP.PARSING && <CenterMsg icon={Loader2} label="Analyse du fichier…" spin />}
                    {step === STEP.PREVIEW && (
                        <PreviewStep
                            counters={counters}
                            items={items}
                            skipDoubleOptin={skipDoubleOptin}
                            onToggleConsent={(v) => setSkipDoubleOptin(v)}
                            filename={filename}
                            onReset={reset}
                        />
                    )}
                    {step === STEP.IMPORTING && (
                        <ImportingStep progress={progress} onCancel={cancel} />
                    )}
                    {step === STEP.DONE && <DoneStep result={result} />}
                    {step === STEP.ERROR && (
                        <ErrorStep message={errorMsg} onRetry={reset} />
                    )}
                    {step === STEP.CANCELLED && (
                        <CenterMsg icon={AlertTriangle} label="Import annulé." color={palette.warning} />
                    )}
                </div>

                <footer style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16, flexShrink: 0 }}>
                    {step === STEP.IDLE && (
                        <button onClick={onClose} style={ghostBtn}>Annuler</button>
                    )}
                    {step === STEP.PREVIEW && (
                        <>
                            <button onClick={reset} style={ghostBtn}>Choisir un autre fichier</button>
                            <button
                                onClick={submit}
                                style={{ ...accentBtn, opacity: counters.ok === 0 ? 0.5 : 1 }}
                                disabled={counters.ok === 0}
                            >
                                <Upload size={13} /> Importer {counters.ok} contact{counters.ok > 1 ? 's' : ''}
                            </button>
                        </>
                    )}
                    {(step === STEP.DONE || step === STEP.CANCELLED) && (
                        <button onClick={onClose} style={accentBtn}>Fermer</button>
                    )}
                    {step === STEP.ERROR && (
                        <>
                            <button onClick={onClose} style={ghostBtn}>Fermer</button>
                            <button onClick={reset} style={accentBtn}>Recommencer</button>
                        </>
                    )}
                </footer>

                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.tsv,.txt,text/csv"
                    onChange={(e) => handleFile(e.target.files?.[0])}
                    style={{ display: 'none' }}
                />
            </div>
        </div>
    );
}

function IdleStep({ dragActive, onDragOver, onDragLeave, onDrop, onPick, parseError }) {
    return (
        <div>
            <div
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
                onClick={onPick}
                style={{
                    border: `2px dashed ${dragActive ? palette.accent : palette.accentBorder}`,
                    background: dragActive ? palette.accentBg : palette.accentSoft,
                    borderRadius: 12, padding: '40px 20px', textAlign: 'center',
                    cursor: 'pointer', transition: 'all 0.15s',
                }}
            >
                <Upload size={32} style={{ color: palette.accent, marginBottom: 12 }} />
                <div style={{ fontSize: 14, fontWeight: 600, color: palette.text, marginBottom: 6 }}>
                    Glisse un fichier CSV ici, ou clique pour parcourir.
                </div>
                <div style={{ fontSize: 12, color: palette.muted, lineHeight: 1.55 }}>
                    Colonnes attendues : <strong>email</strong> (obligatoire), <strong>nom</strong> (optionnel).<br />
                    Séparateurs détectés : virgule, point-virgule, tabulation, pipe.<br />
                    Maximum 10 000 lignes par import.
                </div>
            </div>
            {parseError && (
                <div style={{
                    marginTop: 12, padding: 10, fontSize: 12.5,
                    background: palette.dangerBg, color: palette.danger,
                    border: `1px solid ${palette.danger}33`, borderRadius: 8,
                }}>
                    {parseError}
                </div>
            )}
        </div>
    );
}

function PreviewStep({ counters, items, skipDoubleOptin, onToggleConsent, filename, onReset }) {
    const showRows = items.slice(0, 50);
    return (
        <div>
            <div style={{
                display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14,
                background: palette.accentSoft, border: `1px solid ${palette.accentBorder}`,
                borderRadius: 8, padding: '10px 14px',
            }}>
                <FileText size={14} style={{ color: palette.accent }} />
                <span style={{ fontSize: 12.5, color: palette.text, fontWeight: 500 }}>{filename}</span>
                <span style={{ flex: 1 }} />
                <span style={{ fontSize: 11.5, color: palette.muted }}>
                    {counters.total} ligne{counters.total > 1 ? 's' : ''} · <strong style={{ color: palette.success }}>{counters.ok} valides</strong>
                    {counters.dup > 0 && <> · <strong style={{ color: palette.warning }}>{counters.dup} doublons</strong></>}
                    {counters.inv > 0 && <> · <strong style={{ color: palette.danger }}>{counters.inv} invalides</strong></>}
                    {counters.bl > 0 && <> · <strong style={{ color: palette.danger }}>{counters.bl} refusés</strong></>}
                </span>
            </div>

            <div style={{
                border: `1px solid ${palette.cardBorder}`, borderRadius: 8,
                maxHeight: 280, overflowY: 'auto', marginBottom: 14,
            }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                    <thead style={{ position: 'sticky', top: 0, background: palette.cardBg, zIndex: 1 }}>
                        <tr style={{ borderBottom: `1px solid ${palette.cardBorder}` }}>
                            <th style={thStyle}>Email</th>
                            <th style={thStyle}>Nom</th>
                            <th style={{ ...thStyle, textAlign: 'right' }}>État</th>
                        </tr>
                    </thead>
                    <tbody>
                        {showRows.map((it, i) => (
                            <tr key={i} style={{ borderBottom: `1px solid ${palette.cardBorder}` }}>
                                <td style={tdStyle}>{it.email}</td>
                                <td style={{ ...tdStyle, color: palette.muted }}>{it.name || '—'}</td>
                                <td style={{ ...tdStyle, textAlign: 'right' }}>
                                    <StatusChip status={it.status} reason={it.reason} />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {items.length > 50 && (
                    <div style={{ padding: 8, textAlign: 'center', fontSize: 11, color: palette.muted, borderTop: `1px solid ${palette.cardBorder}` }}>
                        … et {items.length - 50} autres lignes (importées si valides)
                    </div>
                )}
            </div>

            <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer', fontSize: 12.5, color: palette.muted, lineHeight: 1.55 }}>
                <input
                    type="checkbox"
                    checked={skipDoubleOptin}
                    onChange={(e) => onToggleConsent(e.target.checked)}
                    style={{ marginTop: 3, accentColor: palette.accent }}
                />
                <span>
                    <strong style={{ color: palette.text }}>Tous les contacts ont donné leur consentement explicite.</strong>{' '}
                    Cocher = abonnement direct (RGPD : tu en attestes). Décocher = double opt-in
                    (chaque contact reçoit un email de confirmation).
                </span>
            </label>
        </div>
    );
}

function ImportingStep({ progress, onCancel }) {
    const pct = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;
    return (
        <div style={{ padding: '20px 8px' }}>
            <div style={{ fontSize: 13.5, color: palette.text, marginBottom: 4, fontWeight: 600 }}>
                Import en cours — {progress.done} / {progress.total}
            </div>
            <div style={{ fontSize: 11.5, color: palette.muted, marginBottom: 14, fontFamily: 'monospace', minHeight: 16 }}>
                {progress.current_email || '…'}
            </div>
            <div style={{ height: 8, borderRadius: 999, background: palette.accentSoft, overflow: 'hidden', marginBottom: 16 }}>
                <div style={{
                    height: '100%', width: `${pct}%`,
                    background: `linear-gradient(90deg, ${palette.accent}, #c084fc)`,
                    transition: 'width 0.3s',
                }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 11, color: palette.muted }}>{pct} %</span>
                <button onClick={onCancel} style={ghostBtn}>Annuler</button>
            </div>
        </div>
    );
}

function DoneStep({ result }) {
    if (!result) return null;
    const skipped = result.skipped || [];
    return (
        <div style={{ padding: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <CheckCircle2 size={20} style={{ color: palette.success }} />
                <span style={{ fontSize: 14, fontWeight: 600, color: palette.text }}>Import terminé</span>
            </div>
            <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: palette.text, lineHeight: 1.7 }}>
                <li><strong style={{ color: palette.success }}>{result.created}</strong> nouveau{result.created > 1 ? 'x' : ''} contact{result.created > 1 ? 's' : ''}.</li>
                <li><strong>{result.attached_existing}</strong> déjà sur listmonk → rattaché{result.attached_existing > 1 ? 's' : ''} à la liste.</li>
                <li><strong>{skipped.length}</strong> ignoré{skipped.length > 1 ? 's' : ''}.</li>
                <li style={{ color: palette.muted }}>Durée : {(result.duration_ms / 1000).toFixed(1)} s.</li>
            </ul>
            {skipped.length > 0 && (
                <details style={{ marginTop: 14, fontSize: 12 }}>
                    <summary style={{ cursor: 'pointer', color: palette.muted }}>Voir les {skipped.length} ignoré{skipped.length > 1 ? 's' : ''}</summary>
                    <ul style={{ marginTop: 8, paddingLeft: 20, color: palette.muted, maxHeight: 180, overflowY: 'auto' }}>
                        {skipped.slice(0, 100).map((s, i) => (
                            <li key={i}>
                                <code style={{ fontFamily: 'monospace' }}>{s.email}</code> — {s.reason}
                                {s.message && <span style={{ marginLeft: 6 }}>({s.message})</span>}
                            </li>
                        ))}
                        {skipped.length > 100 && <li style={{ fontStyle: 'italic' }}>… et {skipped.length - 100} autres</li>}
                    </ul>
                </details>
            )}
        </div>
    );
}

function ErrorStep({ message, onRetry }) {
    return (
        <div style={{ padding: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <AlertCircle size={20} style={{ color: palette.danger }} />
                <span style={{ fontSize: 14, fontWeight: 600, color: palette.text }}>Échec de l'import</span>
            </div>
            <div style={{
                padding: 12, fontSize: 12.5,
                background: palette.dangerBg, color: palette.danger,
                border: `1px solid ${palette.danger}33`, borderRadius: 8,
                marginBottom: 12,
            }}>
                {message || 'Erreur inconnue'}
            </div>
        </div>
    );
}

function CenterMsg({ icon: Icon, label, spin = false, color }) {
    return (
        <div style={{ padding: '40px 20px', textAlign: 'center', color: color || palette.muted }}>
            <Icon size={28} style={{ marginBottom: 12, animation: spin ? 'spin 1s linear infinite' : 'none' }} />
            <div style={{ fontSize: 13.5 }}>{label}</div>
            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </div>
    );
}

function StatusChip({ status, reason }) {
    const conf = {
        ok: { label: 'OK', color: palette.success, bg: palette.successBg },
        duplicate: { label: 'doublon', color: palette.warning, bg: palette.warningBg },
        invalid: { label: 'invalide', color: palette.danger, bg: palette.dangerBg },
        blacklisted: { label: 'refusé', color: palette.danger, bg: palette.dangerBg },
    }[status] || { label: status, color: palette.muted, bg: palette.accentSoft };
    return (
        <span title={reason} style={{
            display: 'inline-block',
            fontSize: 9.5, padding: '2px 7px', borderRadius: 999,
            background: conf.bg, color: conf.color, fontWeight: 600,
            letterSpacing: 0.4, textTransform: 'uppercase',
            border: `1px solid ${conf.color}33`,
        }}>
            {conf.label}
        </span>
    );
}

const accentBtn = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    background: palette.accentBg,
    border: `1px solid ${palette.accentBorder}`, borderRadius: 6,
    padding: '7px 12px',
    color: palette.accent, fontSize: 12, fontWeight: 600,
    cursor: 'pointer', transition: 'all 150ms ease',
};

const ghostBtn = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    background: 'transparent', color: palette.muted,
    border: `1px solid ${palette.cardBorder}`, borderRadius: 6,
    padding: '7px 12px', fontSize: 12, fontWeight: 500, cursor: 'pointer',
};

const thStyle = {
    padding: '8px 12px', textAlign: 'left',
    fontSize: 11, fontWeight: 600, color: palette.muted,
    textTransform: 'uppercase', letterSpacing: 0.5,
};

const tdStyle = {
    padding: '8px 12px', fontSize: 12.5, color: palette.text,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    maxWidth: 240,
};

export default BulkImportModal;
